export default function NRContent() {
  const designElements = [
    { label: "Material", detail: "Wool, jute, cotton, or a wool-cotton blend — natural fibres add warmth and texture that synthetics cannot replicate" },
    { label: "Pile height", detail: "Low to medium pile works best in living rooms — easier to maintain and more versatile with furniture" },
    { label: "Tone", detail: "Warm ivory, oat, sand, or soft taupe — avoid cool grey tones that flatten the room" },
    { label: "Weave", detail: "Flatweave for a more minimal look; looped or tufted for more texture and softness underfoot" },
    { label: "Size", detail: "Go larger than you think — the front legs of all seating should sit on the rug to anchor the space" },
    { label: "Pattern", detail: "Subtle texture or a quiet geometric works better than bold patterns in a calm neutral room" },
  ];

  const stylingTips = [
    "Size up — a rug that is too small makes the room feel disconnected. In a living room, aim for at least 200x300cm.",
    "Place the rug so the front legs of the sofa and chairs sit on it — this visually ties the seating area together.",
    "Layer a smaller flatweave rug over a larger jute base for a relaxed, editorial look.",
    "In a beige or neutral room, choose a rug with a slightly different tone — warm ivory over sand, or oat over cream — to add depth without contrast.",
    "Avoid rugs with cool grey or blue undertones in warm neutral rooms — they will fight the palette rather than support it.",
    "A rug pad underneath extends the life of the rug and keeps it from shifting — worth the investment.",
    "Vacuum regularly but gently — high-pile wool rugs benefit from occasional professional cleaning to maintain their texture.",
  ];

  return (
    <article className="max-w-2xl mx-auto px-4 md:px-0 py-16">

      {/* Introduction */}
      <div className="mb-14">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Introduction
        </p>
        <p
          className="text-[#4A3F38] text-base md:text-lg leading-relaxed mb-5"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          A neutral rug is one of the most impactful things you can add to a room — and one of the most underestimated.
        </p>
        <p
          className="text-[#6B5E53] text-sm md:text-base leading-relaxed"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          The right rug grounds the furniture, softens the acoustics, and adds a layer of warmth that no other element can quite replicate.
          In a calm neutral room, a well-chosen rug is not background noise — it is the foundation everything else rests on.
          This guide covers what to look for, how to size and style it, and which picks we keep returning to.
        </p>
      </div>

      {/* Why Neutral Works */}
      <div className="mb-14">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Why It Works
        </p>
        <h2
          className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          Why a Neutral Rug Looks More Expensive Than a Patterned One
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="relative overflow-hidden rounded-2xl mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/f65a605b0eb71acff75c53ece59e404e.jpg"
            alt="Neutral rug texture and warmth"
            className="w-full h-[280px] md:h-[360px] object-cover object-top"
          />
        </div>

        <p
          className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-5"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          Bold patterns compete with everything else in the room. A neutral rug, by contrast, lets the furniture,
          the light, and the textures do the talking. It creates a sense of calm cohesion that reads as considered and expensive —
          even when the rug itself is not.
        </p>
        <p
          className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-5"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          The key is choosing the right neutral. Warm ivory, oat, and soft sand tones work beautifully in rooms with natural light
          and warm wood. Cool grey or beige-grey rugs can feel flat and clinical — especially in rooms that already lean neutral.
        </p>
        <p
          className="text-[#6B5E53] text-sm md:text-base leading-relaxed"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          Texture is where a neutral rug earns its keep. A looped wool rug, a hand-knotted flatweave, or a tufted cotton blend
          all add visual interest without adding colour — which is exactly what a calm room needs.
        </p>
      </div>

      {/* Key Design Elements */}
      <div className="mb-14">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          What to Look For
        </p>
        <h2
          className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          Key Things to Consider When Choosing a Neutral Rug
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="space-y-4">
          {designElements.map((el, i) => (
            <div key={i} className="flex gap-5 items-start border-b border-[#EDE6DA] pb-4 last:border-0">
              <span
                className="text-[#B5A898] text-[10px] tracking-[0.3em] uppercase w-24 flex-shrink-0 pt-0.5"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {el.label}
              </span>
              <p
                className="text-[#6B5E53] text-sm leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
              >
                {el.detail}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Styling Tips */}
      <div className="mb-14">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Styling
        </p>
        <h2
          className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          How to Style a Neutral Rug
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="relative overflow-hidden rounded-2xl mb-8">
          <img
            src="https://readdy.ai/api/search-image?query=neutral%20rug%20styled%20under%20beige%20sofa%20warm%20living%20room%20light%20wood%20coffee%20table%20linen%20cushions%20calm%20editorial%20interior%20photography%20warm%20light&width=900&height=500&seq=nr_style1&orientation=landscape"
            alt="Neutral rug styled in a calm living room"
            className="w-full h-[280px] md:h-[360px] object-cover object-top"
          />
        </div>

        <ul className="space-y-4">
          {stylingTips.map((tip, i) => (
            <li key={i} className="flex gap-4 items-start">
              <span
                className="text-[#C8BAA8] text-[10px] tracking-[0.3em] mt-1 flex-shrink-0"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                0{i + 1}
              </span>
              <p
                className="text-[#6B5E53] text-sm leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
              >
                {tip}
              </p>
            </li>
          ))}
        </ul>
      </div>

    </article>
  );
}
